/**
 * interfaces
 */
import { IUnitDropdownModel } from './interfaces';
import { IUnitDropdownDto } from '../../interfaces';

/**
 * View model for a unit dropdown item (GET units/dropdown).
 *
 * Wraps the raw {@link IUnitDropdownDto} and exposes every field as a
 * camelCase getter. The {@link isFacility} computed property identifies
 * the root facility node by `level === 0`.
 *
 * @example
 * ```ts
 * const model = new UnitDropdownModel(dto);
 * console.log(model.name);        // "DEMO Facility"
 * console.log(model.isFacility);  // true  (level === 0)
 * console.log(model.typeName);    // "Building"
 * ```
 */
export class UnitDropdownModel implements IUnitDropdownModel {

    /**
     * @param dto - Raw dropdown unit DTO from the API.
     */
    constructor(public readonly dto: IUnitDropdownDto) {}

    /** @inheritdoc */
    public get id(): string {
        return this.dto.id;
    }

    /** @inheritdoc */
    public get name(): string {
        return this.dto.name;
    }

    /** @inheritdoc */
    public get description(): string | null {
        return this.dto.description;
    }

    /** @inheritdoc */
    public get level(): number {
        return this.dto.level;
    }

    /** @inheritdoc */
    public get hasChildren(): boolean {
        return this.dto.has_children;
    }

    /** @inheritdoc */
    public get isBuilding(): boolean {
        return this.dto.is_building;
    }

    /** @inheritdoc */
    public get htmlHexColor(): string | null {
        return this.dto.html_hex_color;
    }

    /** @inheritdoc */
    public get iconId(): string | null {
        return this.dto.icon_id;
    }

    /** @inheritdoc */
    public get latitude(): string {
        return this.dto.latitude;
    }

    /** @inheritdoc */
    public get longitude(): string {
        return this.dto.longitude;
    }

    /** @inheritdoc */
    public get createdOn(): string {
        return this.dto.created_on;
    }

    /** @inheritdoc */
    public get facilityId(): string | null {
        return this.dto.facility_id;
    }

    /** @inheritdoc */
    public get parentId(): string | null {
        return this.dto.parent_id;
    }

    /** @inheritdoc */
    public get typeName(): string {
        return this.dto.type_name;
    }

    /** True when this item is the root facility (level === 0). */
    public get isFacility(): boolean {
        return this.dto.level === 0;
    }

}
